/**
 * FinancialSummaryStrip — formula-driven metric chips at the top of the hover,
 * grouped by EACH date range the formula groups use (so a multi-window formula
 * shows the inputs for every window). Only shows stats the active formula
 * actually consumes; the full Performance section below lists every stat.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';

import { cn } from '@/lib/utils';
import { resolveDateRangeLabel } from '@/features/flow-optimizer/utils/date-range-label';

import {
  collectFormulaMetricIds,
  fmtStatValue,
  metricLabel,
  metricTone,
} from '../../format';
import type { PreviewRow } from '../../../preview-row.types';

export interface FinancialSummaryStripProps {
  row: PreviewRow;
  /** Subformula id -> {expression}, so `__sub_…` refs resolve to real metrics. */
  subFormulas?: ReadonlyMap<string, { name: string; expression: string }>;
}

interface RangeBag {
  dateRange: string;
  chips: Array<[string, number]>;
}

const FinancialSummaryStrip: FC<FinancialSummaryStripProps> = ({
  row,
  subFormulas,
}) => {
  const formulaIds = useMemo(
    () => collectFormulaMetricIds(row.breakdown, subFormulas),
    [row.breakdown, subFormulas],
  );
  const ranges = useMemo(() => buildRangeBags(row, formulaIds), [row, formulaIds]);
  if (ranges.length === 0) return null;
  const showLabels = ranges.length > 1;
  return (
    <div role="group" aria-label="Formula metrics" className="space-y-1">
      {ranges.map((range) => (
        <div
          key={range.dateRange}
          className="rounded border border-border/50 bg-muted/30 px-2 py-1.5"
        >
          {showLabels && (
            <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
              {resolveDateRangeLabel(range.dateRange)}
            </p>
          )}
          <div className="flex flex-wrap gap-1">
            {range.chips.map(([id, value]) => (
              <Chip key={id} id={id} value={value} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/** Formula metrics per distinct date range used by the groups (in config order). */
function buildRangeBags(row: PreviewRow, formulaIds: string[]): RangeBag[] {
  const byRange = row.metricsByDateRange;
  if (!byRange || formulaIds.length === 0) return [];
  const seen = new Set<string>();
  const out: RangeBag[] = [];
  for (const entry of row.breakdown ?? []) {
    const dr = entry.dateRange;
    if (!dr || seen.has(dr)) continue;
    seen.add(dr);
    const bag = byRange[dr];
    if (!bag) continue;
    const chips = formulaIds
      .filter((id) => id in bag)
      .map((id) => [id, bag[id]] as [string, number]);
    if (chips.length > 0) out.push({ dateRange: dr, chips });
  }
  return out;
}

const Chip: FC<{ id: string; value: number }> = ({ id, value }) => (
  <span className="inline-flex items-baseline gap-1 rounded border border-border/40 bg-background px-1.5 py-0.5 text-[10px] tabular-nums">
    <span className="text-muted-foreground">{metricLabel(id)}</span>
    <span className={cn('font-semibold', metricTone(id, value))}>
      {fmtStatValue(id, value)}
    </span>
  </span>
);

export default FinancialSummaryStrip;
